// Login-error funnel — ONE call per login/auth failure, shipping it two ways:
//
//   1. `logger.error('auth.login_error', payload)` → Better Stack browser log
//      row (+ Errors entry) for drill-down on the full detail.
//   2. Same-origin beacon → POST /api/auth-telemetry → OTel counter
//      `frontend.auth_login_error_total`. The browser Better Stack tag's
//      events are NOT queryable as metrics, so this counter is what the
//      rewards dashboard charts and the login-error alert fires on (same
//      first-party pattern as /api/web-vitals).
//
// Statsig's `auth_error` product event is emitted separately (SDK bridge).

import type { AuthErrorProperties } from '@/events';
import { logger } from '@/lib/telemetry/logger';
import { scrubGrantTokens } from '@/lib/telemetry/scrub';

const ENDPOINT = '/api/auth-telemetry';
/** Cap on the serialized detail payload — enough for a cause chain + flow
 *  context, small enough that sendBeacon never rejects the body. Shared with
 *  the receiving route so the wire contract has one owner. */
export const DETAIL_MAX_CHARS = 2000;

/** The typed `auth_error` fields plus any extra flow context callers attach
 *  (the callback hang adds `flow`/`had_code`/`dest` etc. for the log row). */
export type LoginErrorPayload = Partial<AuthErrorProperties> & Record<string, unknown>;

/** Pull a short string field off the payload for the metric's labels. */
function labelField(
  payload: LoginErrorPayload,
  key: 'error' | 'context' | 'provider',
): string | null {
  const v = payload[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Turnkey's kit-level `onError` fires for every modal failure, including the
 * private-key export ("Failed to export wallet / private key / wallet
 * account"). Those are not login failures: they are counted by the export
 * flows themselves (`wallet_export` → `frontend.wallet_export_total`) and must
 * not depress the login success rate.
 */
export function isWalletExportFailure(payload: LoginErrorPayload): boolean {
  const error = labelField(payload, 'error');
  return error !== null && error.startsWith('Failed to export');
}

/**
 * Report a failed login/auth attempt. `payload` should carry at least
 * `error` (message), and ideally `context` (where it surfaced) and
 * `provider` (oauth provider) — see `AuthErrorProperties` in `@/events`.
 */
export function reportLoginError(payload: LoginErrorPayload): void {
  if (isWalletExportFailure(payload)) {
    logger.warn('wallet.export_error', payload);
    return;
  }
  logger.error('auth.login_error', payload);

  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  try {
    // `detail` carries the FULL payload (cause chain, flow/had_code/dest…) to
    // the server, which persists it as a queryable warn log row. The browser
    // tag's `track` sink was found to ship nowhere (2026-07-07: 550+ counted
    // login errors over 4 days, zero detail rows in any Better Stack row
    // type), so without this the counter can say HOW MANY logins fail but
    // never WHY. Scrubbed client-side; size-capped for sendBeacon.
    let detail = '';
    try {
      detail = JSON.stringify(scrubGrantTokens(payload)).slice(0, DETAIL_MAX_CHARS);
    } catch {
      // SILENT-CATCH: unserializable payload — labels alone still ship.
    }
    const body = {
      error: labelField(payload, 'error') ?? 'unknown',
      context: labelField(payload, 'context') ?? 'unknown',
      provider: labelField(payload, 'provider') ?? 'none',
      detail,
    };
    const blob: Blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    navigator.sendBeacon(ENDPOINT, blob);
  } catch {
    // SILENT-CATCH: telemetry must never crash the page.
  }
}
